import axios from 'axios';

/**
 * Dynamic API Configuration with Environment Variable Support
 * This eliminates the need for runtime sed replacements by properly
 * handling environment variables at build time.
 */

// Get API URL from environment variables with smart fallbacks
function getApiUrl() {
  // Priority order:
  // 1. REACT_APP_API_URL (explicit environment variable)
  // 2. Auto-detect based on current window location (for production deployments)
  // 3. Development fallback
  
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Auto-detect API URL based on current hostname and protocol
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    
    // For development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Default development API endpoints
      const isDev = process.env.NODE_ENV === 'development';
      if (isDev) {
        return 'http://localhost:8000';
      }
      // Production build served from localhost
      return protocol === 'https:' ? 'https://localhost' : 'http://localhost:8000';
    }
    
    // For production deployments, use same protocol and hostname
    const apiPort = protocol === 'https:' ? (port === '443' ? '' : '') : (port === '80' ? '' : ':8000');
    return `${protocol}//${hostname}${apiPort}`;
  }
  
  // Server-side rendering fallback
  return 'http://localhost:8000';
}

// Get the base API URL
const baseURL = getApiUrl();

// Ensure the API URL ends with /api
const finalBaseURL = baseURL.endsWith('/api') 
  ? baseURL
  : baseURL.endsWith('/') 
    ? `${baseURL}api` 
    : `${baseURL}/api`;

console.log(`[API Config] Using API URL: ${finalBaseURL}`);
console.log(`[API Config] Environment: ${process.env.NODE_ENV}`);
console.log(`[API Config] REACT_APP_API_URL: ${process.env.REACT_APP_API_URL || 'Not set'}`);

// Axios instance configuration
export const api = axios.create({
  baseURL: finalBaseURL,
  timeout: 600000, // 300 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add authentication token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add request logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle common response scenarios
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Response] ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    // Handle authentication errors
    if (error.response && error.response.status === 401) {
      if (window.location.pathname !== '/login') {
        console.log('[API] Authentication error, redirecting to login');
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
      }
    }
    
    // Log errors
    if (process.env.NODE_ENV === 'development') {
      console.error(`[API Error] ${error.response?.status || 'Network'} ${error.config?.url}`, error.response?.data);
    }
    
    return Promise.reject(error);
  }
);

// Project-related API endpoints
export const projectApi = {
  // Project list retrieval
  getProjects: () => api.get('/projects/'),

  // Project details retrieval
  getProject: (projectId) => api.get(`/projects/${projectId}`),
  
  // Create new project
  createProject: (projectData) => api.post('/projects/', projectData),

  // Update project
  updateProject: (projectId, projectData) => api.put(`/projects/${projectId}`, projectData),

  // Delete project
  deleteProject: (projectId) => api.delete(`/projects/${projectId}`),

  // OCR processing
  processFile: (projectId, formData) => api.post(`/projects/${projectId}/ocr`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),

  // OCR results list
  getOcrResults: (projectId) => api.get(`/projects/${projectId}/results`),

  // OCR result details
  getOcrResult: (projectId, resultId) => api.get(`/projects/${projectId}/results/${resultId}`),

  // Generic project API methods
  get: (path) => api.get(`/projects${path}`),
  post: (path, data) => api.post(`/projects${path}`, data),
  put: (path, data) => api.put(`/projects${path}`, data),
  delete: (path) => api.delete(`/projects${path}`),
};

// User-related API endpoints
export const userApi = {
  // Get user profile
  getProfile: () => api.get('/users/me'),
  
  // Update user information
  updateProfile: (userData) => api.put('/users/me', userData),
  
  // Change password
  changePassword: (passwordData) => api.post('/users/change-password', passwordData),
  
  // Email verification
  verifyEmail: (token) => api.post('/users/verify-email', { token }),
  
  // Password reset request
  requestPasswordReset: (email) => api.post('/users/reset-password-request', { email }),
  
  // Password reset
  resetPassword: (token, newPassword) => api.post('/users/reset-password', { token, new_password: newPassword }),
};

// Export the configured API URL for use in other parts of the application
export const API_BASE_URL = finalBaseURL;
export const API_HOST = baseURL;